import { useState } from 'react';
import { Pressable, useWindowDimensions } from 'react-native';
import { Text, XStack, YStack } from 'tamagui';
import { AccentButton, useBreakpoint, useThemeColors, Icon } from '@kiips/ui';
import { colors } from '../../theme/colors';
import { CardCarousel } from './CardCarousel';
import { PriceSelector } from './PriceSelector';
import {
  additionalFeatures,
  type BillingPeriod,
  CONSUMER_PLANS,
  DEFAULT_TRIAL_DAYS,
  featuredFeatures,
  formatPrice,
  type PlanDescriptor,
  unpricedPlanLabel,
} from '../../lib/pricing-plans';

/**
 * Landing-page plans teaser — a compact cut of the three consumer plans.
 * The full comparison table, Business waitlist tier, and FAQ stay on
 * /pricing.
 *
 * Each priced card carries the same monthly/annual `PriceSelector` shipped
 * on /pricing (ADR-0062): both SKUs are visible side by side, selection is
 * per-card (monthly leads), and the chosen period is handed to the host
 * screen so the /pricing deep link resumes on the selected SKU.
 *
 * On phones the cards are a swipeable carousel; from tablet width up
 * (md breakpoint per ADR-0023) they sit side-by-side. The layout
 * decision uses `useBreakpoint()` per ADR-0078; `useWindowDimensions`
 * stays for the carousel-card pixel-math sizing.
 *
 * Card CTAs are routed by the host screen: Free heads to sign-up, paid
 * plans hand off to /pricing so the checkout / signup-resume flow lives
 * in one place (ADR-0062).
 */
interface LandingPricingSectionProps {
  onSelectPlan: (plan: PlanDescriptor, billing: BillingPeriod) => void;
  onSeeAllPlans: () => void;
}

export function LandingPricingSection({ onSelectPlan, onSeeAllPlans }: LandingPricingSectionProps) {
  const { md } = useBreakpoint();
  const { width } = useWindowDimensions();
  const asRow = md;

  return (
    <YStack paddingTop={56} paddingBottom={8}>
      <YStack maxWidth={1100} width="100%" alignSelf="center" paddingHorizontal={28}>
        <Text fontSize={11} color="$textOnSurface" fontWeight="700" letterSpacing={1.2}>
          PLANS &amp; PRICING
        </Text>
        <Text
          fontSize={28}
          fontWeight="800"
          color="$textOnSurface"
          lineHeight={32}
          marginTop={8}
          $lg={{ fontSize: 40, lineHeight: 46 }}
          $xl={{ fontSize: 48, lineHeight: 54 }}
        >
          Start free.{'\n'}Go Pro when you&apos;re ready.
        </Text>
        <Text fontSize={15} color="$textSubdued" marginTop={12} lineHeight={22} maxWidth={520}>
          Every plan includes the full planner. Upgrade for unlimited trips, live
          travel data, and real-time collaboration.
        </Text>
      </YStack>

      {asRow ? (
        <XStack
          marginTop={28}
          gap={16}
          maxWidth={1100}
          width="100%"
          alignSelf="center"
          alignItems="stretch"
          paddingHorizontal={20}
        >
          {CONSUMER_PLANS.map((plan) => (
            <LandingPlanCard
              key={plan.id}
              plan={plan}
              onPress={(billing) => onSelectPlan(plan, billing)}
            />
          ))}
        </XStack>
      ) : (
        <PlanCarousel viewportWidth={width} onSelectPlan={onSelectPlan} />
      )}

      <Pressable
        onPress={onSeeAllPlans}
        accessibilityRole="button"
        accessibilityLabel="Compare all plans"
        style={({ pressed }) => ({ marginTop: 20, alignSelf: 'center', opacity: pressed ? 0.7 : 1 })}
      >
        <Text fontSize={13} fontWeight="700" color={colors.accentCerulean}>
          Compare all plans &amp; FAQ →
        </Text>
      </Pressable>
    </YStack>
  );
}

function PlanCarousel({
  viewportWidth,
  onSelectPlan,
}: {
  viewportWidth: number;
  onSelectPlan: (plan: PlanDescriptor, billing: BillingPeriod) => void;
}) {
  return (
    <CardCarousel
      viewportWidth={viewportWidth}
      marginTop={28}
      cards={CONSUMER_PLANS.map((plan) => ({
        key: plan.id,
        render: (cardWidth) => (
          <LandingPlanCard
            plan={plan}
            width={cardWidth}
            onPress={(billing) => onSelectPlan(plan, billing)}
          />
        ),
      }))}
    />
  );
}

function LandingPlanCard({
  plan,
  onPress,
  width,
}: {
  plan: PlanDescriptor;
  onPress: (billing: BillingPeriod) => void;
  /** Fixed width for carousel mode; omit to flex-fill a row. */
  width?: number;
}) {
  const theme = useThemeColors();
  const highlighted = plan.highlighted;
  // Each card owns its billing period (monthly-first, ADR-0062) so the
  // selector sits on the price and one card's choice doesn't move another's
  // number — mirrors the pricing.tsx PlanCard.
  const [billing, setBilling] = useState<BillingPeriod>('monthly');
  // The highlighted card sits on a `theme.textOnSurface` background, which
  // inverts per theme — colour its labels from the same source so they stay
  // legible in dark mode (mirrors the pricing.tsx PlanCard).
  const textColor = highlighted ? theme.textInverse : theme.textOnSurface;
  const subColor = highlighted ? theme.textInverse : theme.textSubdued;
  // Curated top 4 (the `featured` bullets) collapsed; the rest reveal behind a
  // toggle so the card stays scannable but the full list is one tap away.
  const topFeatures = featuredFeatures(plan);
  const restFeatures = additionalFeatures(plan);
  const [featuresExpanded, setFeaturesExpanded] = useState(false);
  const ctaLabel = plan.ctaLabel ?? (plan.price ? 'Start free trial' : 'Get started');

  return (
    <YStack
      {...(width == null ? { flex: 1 } : { width })}
      backgroundColor={highlighted ? theme.textOnSurface : theme.surface}
      borderRadius={24}
      padding={24}
      gap={14}
      borderWidth={highlighted ? 0 : 1}
      borderColor={theme.borderMuted}
      // E2E hook (responsive.spec.ts) — react-native-web emits
      // `data-pricing-card`; the carousel→row breakpoint flip asserts on it.
      dataSet={{ pricingCard: 'true' }}
    >
      {highlighted ? (
        <XStack alignSelf="flex-start">
          <Text
            fontSize={10}
            fontWeight="800"
            letterSpacing={1}
            color={colors.accentCerulean}
            backgroundColor={colors.accentCerulean + '22'}
            paddingHorizontal={10}
            paddingVertical={4}
            borderRadius={999}
          >
            MOST POPULAR
          </Text>
        </XStack>
      ) : null}

      <YStack gap={4}>
        <Text fontSize={18} fontWeight="800" color={textColor}>
          {plan.name}
        </Text>
        <Text fontSize={13} color={subColor} opacity={0.85}>
          {plan.tagline}
        </Text>
      </YStack>

      <YStack gap={8}>
        {plan.price ? (
          <PriceSelector
            price={plan.price}
            value={billing}
            onChange={setBilling}
            onDark={highlighted}
          />
        ) : (
          <YStack gap={2}>
            {/* Headline shared with /pricing via unpricedPlanLabel ("$0", not
                "Free", so it doesn't just repeat the plan name). The landing
                card adds the reassurance subcopy /pricing omits. */}
            <Text fontSize={34} fontWeight="800" color={textColor}>
              {unpricedPlanLabel(plan)}
            </Text>
            <Text fontSize={12} color={subColor} opacity={0.7}>
              Free forever · No credit card
            </Text>
          </YStack>
        )}
        {plan.price ? (
          // Spell out the post-trial commitment for the selected SKU so
          // flipping the selector tells the user what they'll be charged on
          // day 8 — mirrors the pricing.tsx trial line.
          <Text fontSize={12} fontWeight="600" color={colors.accentCerulean}>
            {plan.trialDays ?? DEFAULT_TRIAL_DAYS}-day free trial, then{' '}
            {formatPrice(plan.price, billing)} · Cancel anytime
          </Text>
        ) : null}
      </YStack>

      <LandingPlanCta highlighted={highlighted} label={ctaLabel} onPress={() => onPress(billing)} />

      <YStack gap={9} marginTop={2}>
        {topFeatures.map((feature) => (
          <LandingFeatureRow key={feature.label} label={feature.label} textColor={textColor} />
        ))}
        {featuresExpanded
          ? restFeatures.map((feature) => (
              <LandingFeatureRow key={feature.label} label={feature.label} textColor={textColor} />
            ))
          : null}
        {restFeatures.length > 0 ? (
          <Pressable
            onPress={() => setFeaturesExpanded((open) => !open)}
            accessibilityRole="button"
            accessibilityState={{ expanded: featuresExpanded }}
            accessibilityLabel={
              featuresExpanded
                ? `Hide the rest of the ${plan.name} features`
                : `Show all ${plan.name} features`
            }
            style={({ pressed }) => ({ marginTop: 4, opacity: pressed ? 0.7 : 1 })}
          >
            <XStack gap={6} alignItems="center">
              <Text fontSize={13} fontWeight="700" color={colors.accentCerulean}>
                {featuresExpanded
                  ? 'Show less'
                  : `Show all ${topFeatures.length + restFeatures.length} features`}
              </Text>
              <YStack transition="quick" rotate={featuresExpanded ? '180deg' : '0deg'}>
                <Icon name="expand-more" size={18} color={colors.accentCerulean} />
              </YStack>
            </XStack>
          </Pressable>
        ) : null}
      </YStack>
    </YStack>
  );
}

function LandingFeatureRow({ label, textColor }: { label: string; textColor: string }) {
  return (
    <XStack gap={9} alignItems="flex-start">
      <Icon name="check-circle" size={15} color={colors.accentCerulean} style={{ marginTop: 1 }} />
      <Text fontSize={13} color={textColor} lineHeight={18} flex={1}>
        {label}
      </Text>
    </XStack>
  );
}

function LandingPlanCta({
  highlighted,
  label,
  onPress,
}: {
  highlighted: boolean;
  label: string;
  onPress: () => void;
}) {
  if (highlighted) {
    return (
      <AccentButton onPress={onPress} accessibilityLabel={label}>
        {label}
      </AccentButton>
    );
  }
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      style={({ pressed }) => ({
        borderWidth: 1,
        borderColor: colors.accentCerulean,
        paddingVertical: 12,
        borderRadius: 999,
        alignItems: 'center',
        opacity: pressed ? 0.7 : 1,
      })}
    >
      <Text fontSize={14} fontWeight="700" color={colors.accentCerulean}>
        {label}
      </Text>
    </Pressable>
  );
}
